import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { faAnglesLeft } from '@fortawesome/free-solid-svg-icons';
import { Box, styled, SwipeableDrawer, useMediaQuery } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import { useSidePanel } from './GridTsoPq';

type SidePanelLayoutProps = {
  readonly firstSlot: ReactNode;
  readonly secondSlot?: ReactNode;
  readonly sectionTitle: string;
  readonly actionSlot?: ReactElement;
};

const calculatedHeight = 'calc(100vh-2.625rem)';

const StyledSidePanelContainer = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flexShrink: '0',
    height: calculatedHeight,
    maxHeight: calculatedHeight,
    padding: tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_24,
    backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
  };
});

const StyledSidePanelSlotsContainer = styled(Box)(() => {
  return {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4,
  };
});

const BaseSlot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  alignSelf: 'stretch',
  gap: '0.5rem',
}));

const StyledSidePanelSlotFullHeight = styled(BaseSlot)(() => ({
  height: '100%',
}));

const StyledSidePanelTitle = styled(Box)(() => {
  return {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_16,
  };
});
const StyledActionSlotContainer = styled(Box)(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
});

export const SidePanelTsoPq = ({ firstSlot, secondSlot, sectionTitle, actionSlot }: SidePanelLayoutProps) => {
  const { isOpened, handleOpen } = useSidePanel();
  const isSplit = Boolean(secondSlot);

  const isSmallerThanDesktopScreen = useMediaQuery(tsoPqTheme.breakpoints.down('desktop'));

  return (
    <>
      {isSmallerThanDesktopScreen ? (
        <SwipeableDrawer
          sx={{ width: '37.5rem' }}
          anchor={'left'}
          open={!!isOpened}
          onClose={() => handleOpen?.(false)}
          onOpen={() => handleOpen?.(true)}
        >
          <StyledSidePanelContainer>
            <StyledSidePanelTitle>
              <TypographyTsoPq typographyType="sectionTitleMDBold">{sectionTitle}</TypographyTsoPq>
              {isSmallerThanDesktopScreen && (
                <IconButtonTsoPq
                  icon={faAnglesLeft}
                  buttonType="outlinedPrimary"
                  sizeVariant="s"
                  onClick={() => handleOpen(false)}
                  sx={{ position: 'absolute', right: '1.5rem', top: '1.5rem' }}
                />
              )}
              <StyledActionSlotContainer>{actionSlot}</StyledActionSlotContainer>
            </StyledSidePanelTitle>
            <StyledSidePanelSlotsContainer>
              {isSplit ? (
                <>
                  {firstSlot}
                  {secondSlot}
                </>
              ) : (
                <StyledSidePanelSlotFullHeight>{firstSlot}</StyledSidePanelSlotFullHeight>
              )}
            </StyledSidePanelSlotsContainer>
          </StyledSidePanelContainer>
        </SwipeableDrawer>
      ) : (
        <StyledSidePanelContainer>
          <StyledSidePanelTitle>
            <TypographyTsoPq typographyType="sectionTitleMDBold">{sectionTitle}</TypographyTsoPq>
            <StyledActionSlotContainer>{actionSlot}</StyledActionSlotContainer>
          </StyledSidePanelTitle>
          <StyledSidePanelSlotsContainer>
            {isSplit ? (
              <>
                {firstSlot}
                {secondSlot}
              </>
            ) : (
              <StyledSidePanelSlotFullHeight>{firstSlot}</StyledSidePanelSlotFullHeight>
            )}
          </StyledSidePanelSlotsContainer>
        </StyledSidePanelContainer>
      )}
    </>
  );
};
